import React, { useEffect, useRef, useState } from "react";
import styles from "./AdLanding.module.css";

const cities = [
  "서울", "부산", "대구", "인천", "광주", "대전", "울산", "세종",
  "경기", "강원", "충북", "충남", "전북", "전남", "경북", "경남", "제주",
];

const clients = [
  { name: "용용선생", tag: "모던중식", href: "#portfolio" },
  { name: "두찜", tag: "로제찜닭", href: "#portfolio" },
  { name: "청도미나리식당", tag: "미나리샤브", href: "#portfolio" },
  { name: "샤브인서울", tag: "샤브샤브", href: "#portfolio" },
  { name: "하림", tag: "브랜드", href: "#portfolio" },
];

const faqs = [
  {
    q: "우리 매장과 가까운 ‘로컬 고객’만 타게팅 가능한가요?",
    a: "가능합니다. 시/군/구 단위의 하이퍼로컬 타게팅과 관심사·나이·시간대 등 복합 타게팅을 조합해 실제 방문 가능성이 높은 오디언스를 중심으로 집행합니다.",
  },
  {
    q: "SNS만 하면 되지, 블로그까지 꼭 필요한가요?",
    a: "대부분의 방문 의사결정은 ‘탐색(SNS/블로그) → 지도(리뷰·사진 확인) → 방문’으로 이어집니다. 한 채널만 하면 중간 단계에서 이탈이 큽니다. 저희는 SNS·블로그 양측 노출과 네이버 플레이스 작업까지 동시에 수행합니다.",
  },
  {
    q: "네이버 플레이스는 무엇을 작업하나요?",
    a: "키워드/카테고리·운영정보 정비, 사진/메뉴/리뷰 하이라이트, 이벤트·콘텐츠 연결, 성과 측정을 위한 UTM/트래킹 등을 수행합니다.",
  },
  {
    q: "인플루언서는 어떻게 운영하나요?",
    a: "전국 1,000+ 지역 기반 크리에이터 풀에서 업종·지역·톤앤매너를 매칭하여 캠페인을 구성합니다. 과도한 광고 어투를 지양하고 ‘방문해보고 싶은’ 컨셉으로 제작합니다.",
  },
  {
    q: "계약은 어떻게 진행되나요?",
    a: "파일럿 1개월 진행 → 적합성 검증 → 월단위 계약으로 전환합니다. 장기약정/위약금 없이 투명 리포팅을 제공합니다.",
  },
];

function useInViewAnimation(selector) {
  useEffect(() => {
    const els = document.querySelectorAll(selector);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add(styles.in);
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [selector]);
}

function Counter({ end, duration = 1200, suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let raf; let start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * end));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);
  return (
    <span ref={ref} className={styles.counter}>
      {val.toLocaleString()}<span className={styles.suffix}>{suffix}</span>
    </span>
  );
}

export default function AdLanding() {
  useInViewAnimation(`.${styles.reveal}`);

  const [open, setOpen] = useState(null);

  return (
    <div className={styles.page}>
      {/* NAV */}
      <header className={styles.nav}>
        <a className={styles.logo} href="#top" aria-label="LOCALPLACE">LOCALPLACE</a>
        <nav className={styles.navlinks}>
          <a href="#why">로컬의 본질</a>
          <a href="#pipeline">파이프라인</a>
          <a href="#place">네이버 플레이스</a>
          <a href="#portfolio">포트폴리오</a>
          <a href="#contact" className={styles.ctaSm}>상담하기</a>
        </nav>
      </header>

      {/* HERO */}
      <section id="top" className={`${styles.hero} ${styles.reveal}`}>
        <div className={styles.heroInner}>
          <h1>
            매장 광고의 본질은 <em>로컬</em>입니다
          </h1>
          <p>
            전국 노출이 아니라, <strong>실제로 방문할 수 있는 사람</strong>에게 닿아야 합니다.
            전국 1,000+ <em>지역 크리에이터 네트워크</em>와
            <strong> SNS·블로그↔네이버 지도</strong>를 잇는 이중 파이프라인으로
            방문 전환을 끝까지 밀어붙입니다.
          </p>
          <div className={styles.heroCtas}>
            <a href="#contact" className={styles.ctaPrimary}>지금 상담받기</a>
            <a href="#portfolio" className={styles.ctaGhost}>결과 먼저 보기</a>
          </div>
          <ul className={styles.heroBullets}>
            <li>하이퍼로컬 타게팅 (시/군/구 + 관심사/시간대)</li>
            <li>전국 1,000+ 지역 인플루언서/블로거 액티베이션</li>
            <li>SNS·블로그 노출 + 네이버 플레이스 최적화 병행</li>
          </ul>
        </div>
        <div className={styles.ticker} aria-hidden>
          <div className={styles.tickerTrack}>
            {clients.concat(clients).map((c, i) => (
              <a key={i} href={c.href} className={styles.tickerItem}>
                <span className={styles.badge}>CASE</span>
                <b>{c.name}</b>
                <span className={styles.dim}>#{c.tag}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF STATS */}
      <section className={`${styles.stats} ${styles.reveal}`}>
        <div className={styles.statCard}>
          <Counter end={100} suffix="만+" />
          <span>월 간 도달/노출</span>
        </div>
        <div className={styles.statCard}>
          <Counter end={1000} suffix="+" />
          <span>로컬 크리에이터 풀</span>
        </div>
        <div className={styles.statCard}>
          <Counter end={18} suffix="%p" />
          <span>평균 예약·방문 전환 상승</span>
        </div>
        <div className={styles.statCard}>
          <Counter end={2} suffix="배" />
          <span>콘텐츠 유입 대비 지도 유입</span>
        </div>
      </section>

      {/* WHY LOCAL */}
      <section id="why" className={`${styles.why} ${styles.reveal}`}>
        <div className={styles.sectionHead}>
          <h2>왜 ‘로컬’이 본질일까요?</h2>
          <p>매장 방문은 물리적 거리의 제약을 받습니다. 그래서 <strong>지역 단위 타게팅</strong>과 <strong>현지 창작자</strong>가 중요합니다.</p>
        </div>
        <div className={styles.grid3}>
          <div className={styles.card}> 
            <h3>1) 하이퍼로컬 타게팅</h3>
            <p>시/군/구·생활권·시간대·관심사를 조합해 <em>‘올 수 있는 사람’</em>에게만 보입니다.</p>
          </div>
          <div className={styles.card}>
            <h3>2) 지역 크리에이터 1,000+</h3>
            <p>지역 감도·톤앤매너를 이해하는 <strong>로컬 인플루언서·블로거</strong>가 제작/확산합니다.</p>
          </div>
          <div className={styles.card}>
            <h3>3) 방문 중심 메시지</h3>
            <p>‘저장·길찾기’가 나오는 <strong>지점/시간·메뉴·동선</strong> 정보 중심으로 스토리텔링합니다.</p>
          </div>
        </div>
        <div className={styles.regionMarquee}>
          <div className={styles.regionTrack}>
            {cities.concat(cities).map((c, i) => (
              <span key={i} className={styles.regionPill}>{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PIPELINE */}
      <section id="pipeline" className={`${styles.pipeline} ${styles.reveal}`}>
        <div className={styles.sectionHead}>
          <h2>탐색 → 지도 → 방문, <em>이중 파이프라인</em>으로 연결</h2>
          <p>‘탐색(SNS/블로그)’과 ‘검증(지도/리뷰)’ 두 축을 동시에 강화해 방문 전환을 설계합니다.</p>
        </div>
        <div className={styles.pipelineWrap}>
          <div className={styles.lane}>
            <h3>Lane A — SNS·블로그 노출</h3>
            <ul>
              <li>지역 크리에이터·최적화 블로그 다계정 동시 업로드</li>
              <li>콘셉트·시그니처 메뉴·동선(주차/대기/예약) 강조</li>
              <li>UGC 확산·해시태그·저장 유도</li>
            </ul>
          </div>
          <div className={styles.laneArrow} aria-hidden>⇄</div>
          <div className={styles.lane}>
            <h3>Lane B — 네이버 지도 전환</h3>
            <ul>
              <li>플레이스 정보 정비(키워드/카테고리/운영정보)</li>
              <li>리뷰/사진 큐레이션·메뉴/이벤트 업데이트</li>
              <li>길찾기/전화/예약 버튼 클릭 최적화</li>
            </ul>
          </div>
        </div>
        <div className={styles.pipelineCards}>
          <div className={styles.pCard}>
            <h4>전환 설계</h4>
            <p>콘텐츠→지도→방문 흐름에 <strong>UTM/클릭추적</strong>을 연결, 누수 지점을 즉시 보완합니다.</p>
          </div>
          <div className={styles.pCard}>
            <h4>콘텐츠 운영</h4>
            <p>쇼츠/릴스·캐러셀·블로그를 조합해 <strong>검색·탐색·저장</strong> 모두를 커버합니다.</p>
          </div>
          <div className={styles.pCard}>
            <h4>리포팅</h4>
            <p>도달·저장·지도유입·전화·길찾기 등을 <strong>하나의 대시보드</strong>로 제공합니다.</p>
          </div>
        </div>
      </section>

      {/* PLACE OPTIMIZATION */}
      <section id="place" className={`${styles.place} ${styles.reveal}`}>
        <div className={styles.sectionHead}>
          <h2>네이버 플레이스, <em>방문 전환 허브</em>로 만듭니다</h2>
          <p>지도 검색에서 보이는 것만으로도 방문 의사가 달라집니다. 사진·리뷰·정보를 ‘방문 친화’ 형태로 재구성합니다.</p>
        </div>
        <div className={styles.grid3}>
          <div className={styles.cardIcon}>
            <div className={styles.iconCircle}>🔎</div>
            <h3>키워드 & 카테고리</h3>
            <p>지역/상권 키워드를 반영하고 실제 고객이 찾는 표현으로 정비합니다.</p>
          </div>
          <div className={styles.cardIcon}>
            <div className={styles.iconCircle}>🖼️</div>
            <h3>사진 & 메뉴</h3>
            <p>대표/메뉴/공간 사진의 첫인상과 순서를 재배치해 ‘저장→방문’으로 이어지게 합니다.</p>
          </div>
          <div className={styles.cardIcon}>
            <div className={styles.iconCircle}>⭐</div>
            <h3>리뷰 하이라이트</h3>
            <p>신뢰도 높은 리뷰와 키워드 리뷰를 상단에 노출, 핵심 장점을 빠르게 전달합니다.</p>
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section id="portfolio" className={`${styles.portfolio} ${styles.reveal}`}>
        <div className={styles.sectionHead}>
          <h2>성과 사례</h2>
          <p>파일럿 4주 만에 ‘예약 1.6배’, 오픈 초기 ‘웨이팅 발생’ 등 다수 사례를 보유하고 있습니다.</p>
        </div>
        <div className={styles.scrollRow}>
          {clients.map((c, i) => (
            <article key={i} className={styles.case}>
              <div className={styles.caseImg} aria-hidden />
              <div className={styles.caseBody}>
                <h3>{c.name}</h3>
                <p className={styles.dim}>#{c.tag}</p>
                <ul className={styles.kpis}>
                  <li><b>+1700</b><span>만원/2개월</span></li>
                  <li><b>+1.8배</b><span>예약 전환</span></li>
                  <li><b>2등</b><span>핵심 키워드 순위</span></li>
                </ul>
                <a href="#contact" className={styles.ctaSm}>유사 매장 상담</a>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className={`${styles.diff} ${styles.reveal}`}>
        <div className={styles.sectionHead}>
          <h2>우리가 다른 점</h2>
          <p>‘조회수’가 아닌 <strong>방문·매출</strong> 기준으로 설계합니다.</p>
        </div>
        <div className={styles.grid3}>
          <div className={styles.card}>매출 중심 전환 설계</div>
          <div className={styles.card}>하이퍼로컬 타게팅</div>
          <div className={styles.card}>SNS·블로그 ↔ 지도 연동</div>
          <div className={styles.card}>1,000+ 로컬 크리에이터</div>
          <div className={styles.card}>투명 대시보드 리포팅</div>
          <div className={styles.card}>월단위·파일럿 운영</div>
        </div>
      </section>

      {/* PROCESS */}
      <section className={`${styles.process} ${styles.reveal}`}>
        <div className={styles.sectionHead}>
          <h2>진행 프로세스</h2>
        </div>
        <ol className={styles.steps}>
          <li><b>01 진단</b><span>상권/경쟁/플레이스/자산 진단</span></li>
          <li><b>02 전략</b><span>타게팅·콘셉트·콘텐츠·지표 설계</span></li>
          <li><b>03 제작·배포</b><span>크리에이터 매칭·다채널 업로드</span></li>
          <li><b>04 플레이스</b><span>지도 정보/리뷰/사진 최적화</span></li>
          <li><b>05 분석·개선</b><span>대시보드 리포트·AB테스트</span></li>
        </ol>
      </section>

      {/* CTA BAR */}
      <div className={styles.ctaBar}>
        <div className={styles.ctaBarText}>
          <b>계약 전 파일럿 4주</b>로 먼저 체감해보세요.
        </div>
        <a href="#contact" className={styles.ctaPrimary}>빠른 견적/상담</a>
      </div>

      {/* FAQ */}
      <section className={`${styles.faq} ${styles.reveal}`}>
        <div className={styles.sectionHead}><h2>FAQ</h2></div>
        <ul className={styles.faqList}>
          {faqs.map((f, i) => (
            <li key={i} className={styles.faqItem}>
              <button onClick={() => setOpen(open === i ? null : i)} aria-expanded={open===i}>
                {f.q}
                <span className={styles.plus}>{open===i?"−":"+"}</span>
              </button>
              <div className={`${styles.answer} ${open===i ? styles.show : ""}`}>{f.a}</div>
            </li>
          ))}
        </ul>
      </section>

      {/* CONTACT */}
      <section id="contact" className={`${styles.contact} ${styles.reveal}`}>
        <div className={styles.sectionHead}>
          <h2>상담 문의</h2>
          <p>계약하지 않으셔도 괜찮습니다. 매출/마케팅 고민을 부담 없이 남겨주세요.</p>
        </div>
        <form className={styles.form} onSubmit={(e)=>{e.preventDefault(); alert("제출 완료! 담당자가 곧 연락드립니다.");}}>
          <div className={styles.row}>
            <label>성함<input required placeholder="홍길동"/></label>
            <label>연락처<input required placeholder="010-0000-0000"/></label>
          </div>
          <div className={styles.row}>
            <label>매장명<input placeholder="OO카페/식당"/></label>
            <label>지역<input placeholder="예: 서울 마포구"/></label>
          </div>
          <label>현재 상황<textarea placeholder="매출/방문/리뷰/지도 현황, 원하는 방향 등"></textarea></label>
          <div className={styles.formCtas}>
            <button type="submit" className={styles.ctaPrimary}>상담 요청</button>
            <a className={styles.ctaGhost} href="tel:010-0000-0000">전화 연결</a>
          </div>
        </form>
      </section>

      {/* FOOTER */}
      <footer className={styles.footer}>
        <div>© {new Date().getFullYear()} LOCALPLACE. All rights reserved.</div>
        <div className={styles.footerLinks}>
          <a href="#">개인정보처리방침</a>
          <a href="#">서비스이용약관</a>
        </div>
      </footer>
    </div>
  );
}